// ─── Admin Chrome — Live Shell Components ─────────────────────────────────────
//
// Live promotion of AdminChrome.PLANNED.tsx. Components are shell-owned and
// rendered in the header region / full-width banner slot via AppShell props.
//
// All backend connector stubs are null until AppSync expert wires real RTK hooks.
// See useAdminChrome.ts for the full stub inventory.
//
// Design reference: apps/ystnj/src/components/app-shell/AdminChrome.PLANNED.tsx
// Data-testid contracts are stable selectors — do NOT rename without a migration note.
// ─────────────────────────────────────────────────────────────────────────────

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { IconUserCog } from '@tabler/icons-react';
import { useYstReducedMotion, ystMotionTokens } from '../motion/ystMotion';
import { SupportErrorMessage } from '../overlays/EngineeringSupportBanner';
import type {
  ImpersonationSession,
  TenantSchool,
  ViewAsContext,
} from '../../state/useAdminChrome';

// ─── AdminContextChip ─────────────────────────────────────────────────────────
//
// Always renders for administrator / super-administrator personas. Shows avatar initial
// + email. When isViewAs is true, the ring colour shifts to primary to signal
// that the displayed identity is not the operator's own account.
//
// Selector: data-testid="admin-effective-context"

type AdminContextChipProps = {
  userName: string;
  userEmail: string;
  isViewAs?: boolean;
};

export function AdminContextChip({
  userName,
  userEmail,
  isViewAs = false,
}: AdminContextChipProps) {
  const initial = userName.charAt(0).toUpperCase() || '?';

  return (
    <div
      data-testid="admin-effective-context"
      className={`flex items-center gap-2 rounded-full border bg-muted px-3 py-1 ${isViewAs ? 'border-primary' : 'border-border'}`}
    >
      <span
        className={`flex h-6 w-6 shrink-0 select-none items-center justify-center rounded-full text-[10px] font-bold ${isViewAs ? 'bg-primary/30 text-primary' : 'bg-primary/20 text-primary'}`}
      >
        {initial}
      </span>
      <span className="max-w-[160px] truncate text-xs font-medium text-irongrey">
        {userEmail || userName}
      </span>
    </div>
  );
}

// ─── AdminContextSwitcherLive ─────────────────────────────────────────────────
//
// Combobox for switching tenant/school context. When schools is null or empty
// (connector not yet wired), renders a graceful "context switching unavailable"
// chip. When schools.length === 1, renders a read-only label — no switcher needed.
//
// Selector: data-testid="admin-context-switcher"

type AdminContextSwitcherLiveProps = {
  schools: TenantSchool[] | null;
  activeSchoolId: string | null;
  onSwitchSchool: (schoolId: string) => void;
};

export function AdminContextSwitcherLive({
  schools,
  activeSchoolId,
  onSwitchSchool,
}: AdminContextSwitcherLiveProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Connector not yet wired — graceful unavailable state
  if (schools === null) {
    return (
      <span
        data-testid="admin-context-switcher"
        className="rounded-md border border-border bg-muted px-3 py-1.5 text-xs text-text-tertiary"
      >
        Context switching unavailable
      </span>
    );
  }

  // Single school — read-only label, no switcher needed
  if (schools.length === 1) {
    const only = schools[0];
    return (
      <span
        data-testid="admin-context-switcher"
        className="rounded-md border border-border bg-surface-light px-3 py-1.5 text-sm text-irongrey"
      >
        {only.schoolName}
      </span>
    );
  }

  const activeSchool =
    schools.find((s) => s.schoolId === activeSchoolId) ?? null;

  const filtered = query
    ? schools.filter((s) =>
        s.schoolName.toLowerCase().includes(query.toLowerCase()),
      )
    : schools;

  const handleSelect = (schoolId: string) => {
    onSwitchSchool(schoolId);
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const firstItem = listRef.current?.querySelector(
        'button',
      ) as HTMLButtonElement | null;
      firstItem?.focus();
    }
  };

  return (
    <div data-testid="admin-context-switcher" className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-label="Switch school context"
        placeholder="Switch school…"
        value={isOpen ? query : (activeSchool?.schoolName ?? '')}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          // Delay to allow list-item clicks to fire before blur dismisses
          setTimeout(() => {
            setIsOpen(false);
            setQuery('');
          }, 150);
        }}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleInputKeyDown}
        className="w-[220px] rounded-md border border-border bg-white px-3 py-1.5 text-sm text-irongrey placeholder:text-text-tertiary focus:outline-none"
      />

      {isOpen && filtered.length > 0 ? (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Available schools"
          className="absolute left-0 top-full z-50 mt-1 max-h-48 w-[260px] overflow-y-auto rounded-md border border-border bg-white shadow-md"
        >
          {filtered.map((school) => (
            <li
              key={school.schoolId}
              role="option"
              aria-selected={school.schoolId === activeSchoolId}
            >
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()} // prevent blur before click
                onClick={() => handleSelect(school.schoolId)}
                className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-background-light focus:bg-background-light focus:outline-none ${school.schoolId === activeSchoolId ? 'font-semibold text-primary' : 'text-irongrey'}`}
              >
                {school.schoolName}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ─── AdminImpersonationBannerLive ─────────────────────────────────────────────
//
// Full-width banner rendered below the app header for the duration of an
// impersonation session. Renders NOTHING when session is null (current state
// until backend connector is wired).
//
// Visual: bg-jungle border-b-2 border-strawberry — high-contrast, unmistakable.
// Selector: data-testid="admin-impersonation-banner"
// Reason sub-band: data-testid="admin-impersonation-reason"

type AdminImpersonationBannerLiveProps = {
  session: ImpersonationSession | null;
  onClearSession: () => void;
};

const SUPER_ADMIN_ROLE_OPTIONS = [
  { targetPersona: 'administrator', label: 'Administrator' },
  { targetPersona: 'staff', label: 'Staff' },
  { targetPersona: 'parent', label: 'Parent' },
] as const;

type SuperAdminTargetPersona =
  (typeof SUPER_ADMIN_ROLE_OPTIONS)[number]['targetPersona'];

type RoleMenuPosition = Pick<CSSProperties, 'left' | 'bottom'>;

const roleLabelFor = (targetPersona?: string | null): string =>
  SUPER_ADMIN_ROLE_OPTIONS.find(
    (option) => option.targetPersona === targetPersona,
  )?.label ?? 'Selected role';

export function AdminImpersonationBannerLive({
  session,
  onClearSession,
}: AdminImpersonationBannerLiveProps) {
  const reducedMotion = useYstReducedMotion();
  const roleLabel = session?.targetPersona
    ? roleLabelFor(session.targetPersona)
    : null;

  return (
    <AnimatePresence>
      {session !== null ? (
        <motion.div
          key="impersonation-banner"
          initial={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
          transition={{
            duration: reducedMotion
              ? ystMotionTokens.duration.reducedOpacity
              : ystMotionTokens.duration.micro,
          }}
          data-testid="admin-impersonation-banner"
          className="w-full border-b-2 border-strawberry bg-jungle text-white"
        >
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3 text-sm font-medium">
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold uppercase tracking-widest">
                {roleLabel ? 'Viewing as' : 'Impersonating'}
              </span>
              <span>
                {roleLabel ??
                  `${session.effectiveUserName} (${session.effectiveUserEmail})`}
              </span>
              <span className="text-xs text-white/60">
                Session started {session.startedAt}
              </span>
            </div>
            <button
              type="button"
              data-testid="admin-clear-context"
              onClick={onClearSession}
              className="rounded-lg bg-white/15 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/25"
            >
              {roleLabel ? 'Exit role' : 'Exit impersonation'}
            </button>
          </div>
          {session.reason ? (
            <div
              data-testid="admin-impersonation-reason"
              className="border-t border-white/20 bg-jungle/90 px-4 py-1.5 text-xs text-white/80"
            >
              <span className="font-semibold uppercase tracking-wider">
                Reason:{' '}
              </span>
              {session.reason}
            </div>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

// ─── SuperAdminImpersonationWidgetLive ────────────────────────────────────────

type SuperAdminImpersonationWidgetLiveProps = {
  visible: boolean;
  activeSession: ImpersonationSession | null;
  isLaunching?: boolean;
  error?: string | null;
  onLaunchSession: (input: {
    effectiveUserId?: string;
    effectiveUserName?: string;
    effectiveUserEmail?: string;
    targetPersona?: SuperAdminTargetPersona;
    reason: string;
  }) => Promise<void> | void;
  onClearSession: () => Promise<void> | void;
};

export function SuperAdminImpersonationWidgetLive({
  visible,
  activeSession,
  isLaunching = false,
  error = null,
  onLaunchSession,
  onClearSession,
}: SuperAdminImpersonationWidgetLiveProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<RoleMenuPosition | null>(
    null,
  );

  const resolveMenuPosition = (): RoleMenuPosition | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const trigger = triggerRef.current;
    if (!trigger) {
      return null;
    }
    const rect = trigger.getBoundingClientRect();
    const gutter = 8;
    const menuWidth = 224;
    return {
      left: Math.min(
        Math.max(gutter, rect.left),
        Math.max(gutter, window.innerWidth - menuWidth - gutter),
      ),
      bottom: Math.max(gutter, window.innerHeight - rect.top + gutter),
    };
  };

  const syncMenuPosition = () => {
    const nextPosition = resolveMenuPosition();
    if (nextPosition) {
      setMenuPosition(nextPosition);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    syncMenuPosition();

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        (triggerRef.current?.contains(target) ||
          menuRef.current?.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!visible) {
    return null;
  }

  const activeRoleLabel = activeSession?.targetPersona
    ? roleLabelFor(activeSession.targetPersona)
    : null;
  const activeCopy = activeRoleLabel
    ? `Viewing as ${activeRoleLabel}`
    : activeSession
      ? `Impersonating ${activeSession.effectiveUserName}`
      : null;

  const handleLaunch = async (
    targetPersona: SuperAdminTargetPersona,
    label: string,
  ) => {
    setLocalError(null);
    try {
      await onLaunchSession({
        targetPersona,
        reason: `Super Administrator role impersonation: ${label}`,
      });
      setIsOpen(false);
    } catch (nextError) {
      setLocalError(
        nextError instanceof Error
          ? nextError.message
          : 'Unable to launch impersonation.',
      );
    }
  };

  const handleClear = async () => {
    setLocalError(null);
    try {
      await onClearSession();
      setIsOpen(false);
    } catch (nextError) {
      setLocalError(
        nextError instanceof Error
          ? nextError.message
          : 'Unable to clear impersonation.',
      );
    }
  };

  const roleMenu =
    isOpen && menuPosition && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid="super-administrator-role-impersonation-menu"
            role="menu"
            style={menuPosition}
            className="fixed z-[1100] w-[224px] rounded-lg border border-border bg-white p-2 text-left shadow-lg"
          >
            <div className="px-2 pb-1 text-[11px] font-semibold text-text-tertiary">
              View as role
            </div>
            <div className="flex flex-col gap-1">
              {SUPER_ADMIN_ROLE_OPTIONS.map((option) => (
                <button
                  key={option.targetPersona}
                  type="button"
                  role="menuitem"
                  data-testid={`super-administrator-role-option-${option.targetPersona}`}
                  className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors hover:bg-background-light focus:bg-background-light focus:outline-none ${activeSession?.targetPersona === option.targetPersona ? 'text-primary' : 'text-irongrey'}`}
                  disabled={isLaunching}
                  onClick={() => {
                    void handleLaunch(option.targetPersona, option.label);
                  }}
                >
                  <span>{option.label}</span>
                  {activeSession?.targetPersona === option.targetPersona ? (
                    <span className="text-[11px] font-semibold text-primary">
                      Active
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            {activeCopy ? (
              <div className="mt-2 border-t border-border pt-2">
                <div className="px-2 text-xs font-semibold text-irongrey">
                  {activeCopy}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="super-administrator-role-impersonation-clear"
                  className="mt-1 w-full rounded-md px-2 py-2 text-left text-xs font-semibold text-strawberry transition-colors hover:bg-strawberry/10 focus:bg-strawberry/10 focus:outline-none"
                  disabled={isLaunching}
                  onClick={() => {
                    void handleClear();
                  }}
                >
                  Exit
                </button>
              </div>
            ) : null}
            {localError || error ? (
              <SupportErrorMessage
                data-testid="super-administrator-role-impersonation-error"
                className="mt-2 text-xs"
              >
                {localError ?? error}
              </SupportErrorMessage>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      data-testid="super-administrator-impersonation-widget"
      className="relative inline-flex items-center"
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid="super-administrator-role-impersonation-trigger"
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-irongrey transition-colors hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${activeSession ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-white'}`}
        aria-label={activeCopy ?? 'Open role impersonation menu'}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={activeCopy ?? 'Role impersonation'}
        disabled={isLaunching}
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            return;
          }
          setMenuPosition(resolveMenuPosition());
          setIsOpen(true);
        }}
      >
        <IconUserCog size={17} stroke={1.8} aria-hidden="true" />
      </button>
      {roleMenu}
    </div>
  );
}

// ─── AdminViewAsBannerLive ────────────────────────────────────────────────────
//
// Visually distinct from the impersonation banner — bg-primary/90. Renders
// NOTHING when context is null (current state until backend connector is wired).
//
// Selector: data-testid="admin-view-as-banner"
// Clear button: data-testid="admin-clear-context"

type AdminViewAsBannerLiveProps = {
  context: ViewAsContext | null;
  onClearContext: () => void;
};

export function AdminViewAsBannerLive({
  context,
  onClearContext,
}: AdminViewAsBannerLiveProps) {
  const reducedMotion = useYstReducedMotion();

  return (
    <AnimatePresence>
      {context !== null ? (
        <motion.div
          key="view-as-banner"
          initial={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reducedMotion ? 0 : -8 }}
          transition={{
            duration: reducedMotion
              ? ystMotionTokens.duration.reducedOpacity
              : ystMotionTokens.duration.micro,
          }}
          data-testid="admin-view-as-banner"
          className="flex w-full items-center justify-between border-b-2 border-primary bg-primary/90 px-4 py-2 text-white"
        >
          <div className="flex items-center gap-3 text-sm font-medium">
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold uppercase tracking-widest">
              View as
            </span>
            <span>
              {context.userName} ({context.userEmail})
            </span>
            <span className="rounded-md border border-white/30 px-2 py-0.5 text-xs text-white/80">
              Read-only
            </span>
          </div>
          <button
            type="button"
            data-testid="admin-clear-context"
            onClick={onClearContext}
            className="rounded-lg bg-white/15 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/25"
          >
            Exit view-as
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
